import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Upload, Save, Star, DollarSign, Edit } from 'lucide-react';
import { MentorExampleWorksManager } from './MentorExampleWorksManager';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TIMEZONES, getUserTimezone } from '@/lib/timezone-utils';
// Using public path for default avatar
const defaultAvatar = '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png';

interface MentorProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  bio: string;
  specialty: string;
  expertise: string[];
  credits?: string[];
  hourly_rate: number;
  profile_image_url?: string;
  rating: number;
  total_sessions: number;
  years_experience?: number;
  background_check_approved?: boolean;
  timezone?: string;
}

interface MentorProfileEditorProps {
  mentorId: string;
  isAdmin?: boolean;
  onProfileUpdate?: () => void;
}


export function MentorProfileEditor({ mentorId, isAdmin = false, onProfileUpdate }: MentorProfileEditorProps) {
  const [profile, setProfile] = useState<MentorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expertiseInput, setExpertiseInput] = useState(''); // For raw input during typing
  const [creditsInput, setCreditsInput] = useState(''); // For raw credits input during typing
  const { toast } = useToast();

  // Form state
const [formData, setFormData] = useState({
  first_name: '',
  last_name: '',
  bio: '',
  specialty: '',
  expertise: [] as string[],
  hourly_rate: "" as any, // Allow empty string initially
  years_experience: "" as any, // Allow empty string initially
  timezone: ''
});

  useEffect(() => {
    fetchProfile();
  }, [mentorId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mentors')
        .select('*')
        .eq('id', mentorId)
        .single();

      if (error) throw error;

      setProfile(data);
setFormData({
  first_name: data.first_name || '',
  last_name: data.last_name || '',
  bio: data.bio || '',
  specialty: data.specialty || '',
  expertise: data.expertise || [],
  hourly_rate: data.hourly_rate ? (data.hourly_rate / 100).toString() : "",
  years_experience: data.years_experience || "",
  timezone: data.timezone || getUserTimezone()
});
      
      // Set the raw input display
      setExpertiseInput(Array.isArray(data.expertise) ? data.expertise.join(', ') : '');
      setCreditsInput(Array.isArray(data.credits) ? data.credits.join(', ') : '');
      
      console.log('Profile loaded:', { 
        hasCredits: !!data.credits, 
        creditsType: typeof data.credits,
        creditsValue: data.credits,
        creditsInput: Array.isArray(data.credits) ? data.credits.join(', ') : ''
      });
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch mentor profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - be more specific about supported formats
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!supportedTypes.includes(file.type.toLowerCase())) {
      toast({
        title: 'Invalid File',
        description: 'Please select a JPG, PNG, GIF, or WebP image file',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 5MB',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploading(true);
      
      // Get current user to create folder structure
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Create unique filename with user folder structure
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${mentorId}-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('mentor-profiles')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('mentor-profiles')
        .getPublicUrl(fileName);

      // Update mentor record with new image URL
      const { error: updateError } = await supabase
        .from('mentors')
        .update({ profile_image_url: publicUrl })
        .eq('id', mentorId);

      if (updateError) throw updateError;

      setProfile(prev => prev ? { ...prev, profile_image_url: publicUrl } : null);
      
      toast({
        title: 'Success',
        description: 'Profile image updated successfully'
      });

      onProfileUpdate?.();
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload profile image',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };


  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Convert empty strings to numbers for database, convert dollars to cents
      const dataToSave = {
        ...formData,
        hourly_rate: (Number(formData.hourly_rate) || 0) * 100,
        years_experience: Number(formData.years_experience) || 0,
        credits: Array.isArray(profile?.credits) ? profile.credits : []
      };
      
      const { error } = await supabase
        .from('mentors')
        .update(dataToSave)
        .eq('id', mentorId);

      if (error) throw error;

      setProfile(prev => prev ? { ...prev, ...dataToSave } : null);
      
      toast({
        title: 'Success',
        description: 'Profile updated successfully'
      });

      onProfileUpdate?.();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading profile...</div>;
  }

  if (!profile) {
    return <div>Profile not found</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Edit className="w-5 h-5" />
          {isAdmin ? 'Edit Mentor Profile' : 'Edit Your Profile'}
        </CardTitle>
        <CardDescription>
          Update your public mentor profile information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Profile Image Section */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex flex-col items-center space-y-2 sm:items-start">
            <Avatar className="w-24 h-24">
              <AvatarImage src={profile.profile_image_url || defaultAvatar} />
              <AvatarFallback>
                <img src={defaultAvatar} alt="Default profile" className="w-full h-full object-cover" />
              </AvatarFallback>
            </Avatar>
            <div className="relative">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handleImageUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                className="text-xs px-2 py-1"
              >
                <Upload className="w-3 h-3 mr-1" />
                {uploading ? 'Uploading...' : 'Change Photo'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center sm:text-left max-w-[140px]">
              <strong>Formats:</strong> JPG, PNG, GIF, WebP<br />
              <strong>Max size:</strong> 5MB
            </p>
          </div>
          
          <div className="flex-1 space-y-3 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">{profile.rating.toFixed(1)} rating</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">{profile.total_sessions} sessions</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium text-sm sm:text-base">${(profile.hourly_rate / 100).toFixed(2)}/hour</span>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              value={formData.first_name}
              onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              value={formData.last_name}
              onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
            />
          </div>
        </div>

        {/* Bio */}
        <div>
          <Label htmlFor="bio">Bio</Label>
          <Textarea
            id="bio"
            placeholder="Tell potential students about yourself, your experience, and what you can help them with..."
            value={formData.bio}
            onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
            rows={4}
          />
          <p className="text-sm text-muted-foreground mt-1">
            This will be displayed on your public mentor profile
          </p>
        </div>

        {/* Professional Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <Label htmlFor="specialty">Specialty</Label>
            <Input
              id="specialty"
              placeholder="e.g., Music Production, Audio Engineering"
              value={formData.specialty}
              onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
            <Input
              id="hourly_rate"
              type="number"
              min="0"
              step="1"
              value={formData.hourly_rate || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ ...prev, hourly_rate: value === "" ? "" : Number(value) }));
              }}
            />
          </div>
          <div>
            <Label htmlFor="years_experience">Years of Experience</Label>
            <Input
              id="years_experience"
              type="number"
              min="0"
              max="50"
              value={formData.years_experience || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ ...prev, years_experience: value === "" ? "" : Number(value) }));
              }}
            />
          </div>
        </div>

        {/* Timezone */}
        <div>
          <Label htmlFor="timezone">Timezone</Label>
          <Select
            value={formData.timezone}
            onValueChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
          >
            <SelectTrigger id="timezone" aria-label="Select your timezone">
              <SelectValue placeholder="Select your timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground mt-1">
            Used to convert availability and bookings correctly.
          </p>
        </div>

            {/* Credits & Accolades */}
            <div className="space-y-2">
              <Label htmlFor="credits">Credits & Accolades</Label>
              <Textarea
                id="credits"
                placeholder="e.g., Worked with Drake, Supported by Illenium, Grammy nominated..."
                value={creditsInput}
                onChange={(e) => {
                  // Store the raw input value to preserve spaces while typing
                  setCreditsInput(e.target.value);
                }}
                onBlur={(e) => {
                  // Only process into array on blur to preserve user's typing experience
                  const creditsArray = e.target.value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                  setProfile(prev => prev ? { ...prev, credits: creditsArray } : null);
                }}
                className="min-h-[80px]"
              />
              <p className="text-sm text-muted-foreground">
                List your notable clients, achievements, and accolades separated by commas
              </p>
            </div>

            {/* Expertise */}
        <div>
          <Label htmlFor="expertise">Areas of Expertise</Label>
          <p className="text-sm text-muted-foreground mb-3">
            List your areas of expertise separated by commas
          </p>
          <Textarea
            id="expertise"
            placeholder="e.g., Music Production, Sound Design, Audio Engineering, Mixing & Mastering, DJing, Music Business, Artist Development..."
            value={expertiseInput}
            onChange={(e) => {
              // Store the raw input value to preserve spaces while typing
              setExpertiseInput(e.target.value);
            }}
            onBlur={(e) => {
              // Only process into array on blur to preserve user's typing experience
              const expertiseArray = e.target.value.split(',').map(s => s.trim()).filter(s => s.length > 0);
              setFormData(prev => ({ ...prev, expertise: expertiseArray }));
            }}
            rows={3}
          />
        </div>

        {/* Background Check Status */}
        {profile.background_check_approved !== undefined && (
          <div className="space-y-3">
            <Label>Background Check Status</Label>
            <div className={`p-4 rounded-lg border ${
              profile.background_check_approved 
                ? 'bg-green-50 border-green-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={profile.background_check_approved ? 'default' : 'secondary'}>
                  {profile.background_check_approved ? 'APPROVED' : 'NOT APPROVED'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {profile.background_check_approved 
                  ? 'Background check approved. You can offer in-person sessions.'
                  : 'Background check approval is managed externally. Contact support if you need assistance.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Example Works */}
        <MentorExampleWorksManager 
          mentorId={mentorId}
        />

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full md:w-auto"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}